'use client';

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

import { Sparkles } from 'lucide-react';

import { cn } from '@kit/ui/utils';

/**
 * The hero's thesis: the product is a thing you ask, not a thing you file.
 *
 * Three questions type themselves out and resolve into the same structured
 * answers the assistant really returns — one built from `get_attention_items`,
 * one from `get_order`, one from `compare_supplier_prices`. Nothing here claims
 * a capability the tool registry does not have.
 */

type Row = {
  title: string;
  detail: string;
  value: string;
  tone?: 'warn' | 'ok';
  flag?: string;
};

type Bar = {
  label: string;
  percent: number;
  value: string;
};

type Scene = {
  question: string;
  /** Split around the figure so the number can carry the accent colour. */
  lead: [string, string, string];
  rows?: Row[];
  bars?: Bar[];
  source?: string;
};

const SCENES: Scene[] = [
  {
    question: "What's late, and what does it cost me?",
    lead: [
      'Two orders are past their ready date. ',
      '$41,180',
      ' of goods affected.',
    ],
    rows: [
      {
        title: 'PO-2291 · Ningbo Homeware',
        detail: 'Ready date was 14 days ago · deposit paid',
        value: '$23,100',
        tone: 'warn',
        flag: '14 days late',
      },
      {
        title: 'PO-2304 · Shantou Plastics',
        detail: 'Ready date was 3 days ago · balance unpaid',
        value: '$18,080',
        tone: 'warn',
        flag: '3 days late',
      },
      {
        title: 'PO-2310 · Foshan Metalworks',
        detail: 'Ships Friday · on schedule',
        value: '$9,400',
        tone: 'ok',
        flag: 'On time',
      },
    ],
  },
  {
    question: 'What did PO-2291 actually cost me per unit?',
    lead: [
      'Landed cost is ',
      '$4.62',
      ' per unit, 49% above the invoice price.',
    ],
    bars: [
      { label: 'Goods', percent: 67, value: '$3.10' },
      { label: 'Freight', percent: 15, value: '$0.71' },
      { label: 'Duty', percent: 10, value: '$0.44' },
      { label: 'Other', percent: 8, value: '$0.37' },
    ],
    source:
      'Read from 3 documents · supplier invoice, forwarder bill, C88 entry',
  },
  {
    question: 'Who is cheapest for the 12oz tumbler right now?',
    lead: [
      'Three suppliers have quoted it. ',
      'Foshan Metalworks',
      ' is $0.24 cheaper landed.',
    ],
    rows: [
      {
        title: 'Foshan Metalworks',
        detail: 'Lead time 32 days · MOQ 3,000',
        value: '$4.38',
        tone: 'ok',
        flag: 'Cheapest',
      },
      {
        title: 'Ningbo Homeware',
        detail: 'Lead time 28 days · MOQ 5,000',
        value: '$4.62',
      },
      {
        title: 'Shantou Plastics',
        detail: 'Lead time 45 days · MOQ 2,000',
        value: '$4.91',
      },
    ],
  },
];

/** Typing cadence, the beat before the answer, and how long an answer holds. */
const TYPE_MS = 34;
const THINK_MS = 900;
const HOLD_MS = 5200;

type Phase = 'typing' | 'thinking' | 'answered';

const MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function subscribeToMotionPreference(onChange: () => void) {
  const query = window.matchMedia(MOTION_QUERY);

  query.addEventListener('change', onChange);

  return () => query.removeEventListener('change', onChange);
}

function readMotionPreference() {
  return window.matchMedia(MOTION_QUERY).matches;
}

export function AskConsole() {
  const [active, setActive] = useState(0);
  const [phase, setPhase] = useState<Phase>('typing');
  const [typed, setTyped] = useState('');
  const [onScreen, setOnScreen] = useState(true);
  // A click means the visitor is driving. The carousel stops for good rather
  // than yanking the answer away from under them a few seconds later.
  const [manual, setManual] = useState(false);

  // Read as an external store rather than in an effect, so the first client
  // render already knows the preference instead of typing one frame and then
  // correcting itself. The server snapshot is `false` because the media query
  // is unanswerable there and animating is the safe default to hydrate into.
  const reduced = useSyncExternalStore(
    subscribeToMotionPreference,
    readMotionPreference,
    () => false,
  );

  const sectionRef = useRef<HTMLDivElement | null>(null);
  const scene = SCENES[active]!;

  // With motion reduced there is no typing and no thinking beat: the question
  // and its answer are simply present. Deriving both rather than driving them
  // through state keeps one state machine instead of two.
  const shown = reduced ? scene.question : typed;
  const shownPhase: Phase = reduced ? 'answered' : phase;

  // Same trick the rest of the homepage uses: start optimistically playing and
  // let the observer only ever pause, so a browser that never reports
  // intersection still animates rather than freezing on the first question.
  useEffect(() => {
    const node = sectionRef.current;

    if (!node) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];

        if (entry) {
          setOnScreen(entry.isIntersecting);
        }
      },
      { threshold: 0.15 },
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  // Typing.
  useEffect(() => {
    if (reduced || phase !== 'typing') {
      return;
    }

    if (typed.length >= scene.question.length) {
      const timer = window.setTimeout(() => setPhase('thinking'), 420);

      return () => window.clearTimeout(timer);
    }

    const timer = window.setTimeout(
      () => setTyped(scene.question.slice(0, typed.length + 1)),
      TYPE_MS,
    );

    return () => window.clearTimeout(timer);
  }, [phase, reduced, scene.question, typed]);

  // The beat where it reads your data.
  useEffect(() => {
    if (phase !== 'thinking') {
      return;
    }

    const timer = window.setTimeout(() => setPhase('answered'), THINK_MS);

    return () => window.clearTimeout(timer);
  }, [phase]);

  // Advancing to the next question. `active` is a dependency because with
  // motion reduced `shownPhase` never changes, so the scene index is the only
  // signal that this effect should arm the next timer.
  useEffect(() => {
    if (shownPhase !== 'answered' || manual || !onScreen) {
      return;
    }

    const timer = window.setTimeout(() => {
      setActive((current) => (current + 1) % SCENES.length);
      setTyped('');
      setPhase('typing');
    }, HOLD_MS);

    return () => window.clearTimeout(timer);
  }, [active, manual, onScreen, shownPhase]);

  const select = useCallback((index: number) => {
    setManual(true);
    setActive(index);
    setTyped('');
    setPhase('typing');
  }, []);

  return (
    <div
      ref={sectionRef}
      /* The ring is a mat, and it is what separates the console from the
         section rather than a deeper fill. On a --section-switch ground the
         chrome bands and an 11%-alpha edge all sit within a few points of the
         section behind them, so the console's top and bottom dissolved and
         the mock read as text printed on the page. Deepening the bands was
         the wrong lever — the band label rides on them in --muted-foreground,
         which is what stops the tint ramp going any lower. A --card mat
         clears the tinted section by 1.24 and gives the whole silhouette an
         edge, which is what was missing. */
      className="border-input bg-card ring-card overflow-hidden rounded-3xl border shadow-[0_2px_4px_rgba(22,22,42,0.05),0_28px_70px_-30px_rgba(22,22,42,0.42)] ring-[6px]"
    >
      <div className="border-border bg-muted flex items-center gap-2 border-b px-4 py-3">
        <span className="bg-input size-2.5 rounded-full" />
        <span className="bg-input size-2.5 rounded-full" />
        <span className="bg-input size-2.5 rounded-full" />
        <span className="text-muted-foreground ml-1.5 text-[11px] font-bold tracking-[0.09em] uppercase">
          SupplyAutomate · Assistant
        </span>
      </div>

      <div className="flex min-h-[252px] flex-col gap-4 p-4 sm:min-h-[352px] sm:p-6">
        <div className="border-input bg-background flex items-center gap-3 rounded-2xl border px-4 py-3.5">
          <Sparkles className="text-primary size-[18px] shrink-0" />

          {/* Deliberately not a live region: the text mutates every 34ms
              across three auto-cycling scenes, so announcing it would flood a
              screen reader with fragments. The questions are reachable as the
              button labels below instead. */}
          <p className="min-w-0 flex-1 text-[15px] leading-[1.4] font-semibold tracking-[-0.012em] break-words whitespace-pre-wrap sm:text-[17px]">
            {shown}
            {shownPhase === 'typing' ? (
              <span className="bg-primary ml-0.5 inline-block h-[1.05em] w-0.5 animate-pulse align-[-0.16em]" />
            ) : null}
          </p>
        </div>

        {shownPhase === 'thinking' ? <Thinking /> : null}

        {shownPhase === 'answered' ? <Answer scene={scene} /> : null}
      </div>

      <div className="border-border bg-muted flex gap-2 overflow-x-auto border-t p-3.5 [scrollbar-width:none] sm:flex-wrap sm:overflow-x-visible sm:px-6">
        {SCENES.map((item, index) => (
          <button
            key={item.question}
            type="button"
            onClick={() => select(index)}
            aria-pressed={index === active}
            className={cn(
              'shrink-0 rounded-full border px-3.5 py-1.5 text-xs font-semibold whitespace-nowrap transition-colors',
              index === active
                ? 'border-primary bg-accent-tint text-accent-deep'
                : 'border-border bg-card text-muted-foreground hover:border-primary hover:text-foreground',
            )}
          >
            {item.question}
          </button>
        ))}
      </div>
    </div>
  );
}

function Thinking() {
  return (
    <p className="text-muted-foreground flex items-center gap-2 text-[13px] font-bold">
      <span className="bg-primary size-1.5 animate-bounce rounded-full [animation-delay:0ms]" />
      <span className="bg-primary size-1.5 animate-bounce rounded-full [animation-delay:140ms]" />
      <span className="bg-primary size-1.5 animate-bounce rounded-full [animation-delay:280ms]" />
      Reading your orders, documents and payments…
    </p>
  );
}

function Answer({ scene }: { scene: Scene }) {
  const [before, figure, after] = scene.lead;

  return (
    <div className="animate-in fade-in flex flex-col gap-3 duration-500">
      <p className="text-[15px] leading-[1.55] font-semibold tracking-[-0.01em]">
        {before}
        <span className="text-accent-deep font-bold">{figure}</span>
        {after}
      </p>

      {scene.rows ? (
        <div className="flex flex-col gap-2">
          {scene.rows.map((row) => (
            <div
              key={row.title}
              className="border-border bg-background grid grid-cols-1 items-center gap-2.5 rounded-xl border px-3.5 py-2.5 min-[560px]:grid-cols-[1fr_auto] min-[560px]:gap-3.5"
            >
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="text-[14px] font-bold tracking-[-0.012em] min-[560px]:truncate">
                  {row.title}
                </span>
                <span className="text-muted-foreground text-[12.5px]">
                  {row.detail}
                </span>
              </div>

              <div className="flex items-center gap-2.5 min-[560px]:justify-end">
                {row.flag ? (
                  <span
                    className={cn(
                      'rounded-full px-2.5 py-0.5 text-[11.5px] font-bold whitespace-nowrap',
                      row.tone === 'ok'
                        ? 'bg-sage-bg text-sage-fg'
                        : 'bg-warn-bg text-warn-fg',
                    )}
                  >
                    {row.flag}
                  </span>
                ) : null}

                <span className="text-[15px] font-bold tabular-nums">
                  {row.value}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {scene.bars ? (
        <div className="flex flex-col gap-2.5">
          {scene.bars.map((bar, index) => (
            <div
              key={bar.label}
              className="grid grid-cols-[76px_1fr_52px] items-center gap-3 text-[13px] sm:grid-cols-[88px_1fr_62px]"
            >
              <span className="text-muted-foreground font-semibold">
                {bar.label}
              </span>

              <span className="border-border bg-muted block h-2.5 overflow-hidden rounded-full border">
                <span
                  className={cn(
                    'block h-full rounded-full',
                    index === 0 ? 'bg-primary' : 'bg-muted-foreground',
                  )}
                  style={{ width: `${bar.percent}%` }}
                />
              </span>

              <span className="text-right font-bold tabular-nums">
                {bar.value}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      {scene.source ? (
        <p className="text-muted-foreground text-[12.5px]">{scene.source}</p>
      ) : null}
    </div>
  );
}
